/* eslint-disable no-console */
import { AspectRatio, Box, Container, Flex, Link, Text } from '@chakra-ui/react';
import { nodeParser } from '@kontent-ai/delivery-node-parser';
import { createRichTextHtmlResolver, Elements } from '@kontent-ai/delivery-sdk';
import { htmlResolver } from 'helpers/htmlResolver';
import { toKebabCase, toTitleCase } from 'helpers/textCase';
import { colorScheme } from 'lib/colorScheme';
import { LinkType } from 'lib/enums/LinkType';
import { ContentPage, FeaturedSpots, Sector, ServiceLine, ThemeTopic } from 'models';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { Translate } from 'next-translate';
import { normalize } from 'pages/api/sitemap';
import { FC, useEffect, useRef, useState } from 'react';
import { withTranslations } from 'store/translations';
import { moduleMaxWidth } from 'themes/overrides';
import { imageFormatter } from 'utils/imageFormatter';

import { IconComp } from '../base/IconComp';
import { Link as CustomLink } from '../base/Link';
import { HyphenResult } from '../hyphen/hyphenation';
import { mainSubtypeForOverviewAutomatic } from './OverviewBlock';
interface FeaturedContentProps {
  model: FeaturedSpots;
}
export const FeaturedContent: FC<FeaturedContentProps> = ({ model }) => {
  const translations = withTranslations();
  const focusRefs = useRef<(HTMLAnchorElement | null)[]>([]);
  const router = useRouter();
  const [currentActive, setCurrentActive] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const wasHovered = useRef(false);
  const overviewPages = [
    translations.multiple('project'),
    translations.multiple('product'),
    translations.multiple('insight'),
  ];
  const headline = model.elements.headline.value;
  const hyphenHeadline = HyphenResult(headline, router.locale);
  const viewAllText = model.elements.cta.value;
  const mediaPosition =
    model.elements.content.linkedItems.length > 1 ? 'right' : model.elements.mediaPosition.value[0].codename;
  const contentPages = model.elements.content.linkedItems;
  const backgroundWidth = model.elements.backgroundWidth.value[0].codename;
  const backgroundColor =
    colorScheme[model.elements.backgroundColor.value[0].codename as keyof typeof colorScheme].base;
  const paddings = model.elements.paddings?.value.map((value) => value.codename);
  const triggeredByButton = useRef(false);
  const colors =
    contentPages?.length === 1 && mediaPosition === 'left' ? ['white', backgroundColor] : [backgroundColor, 'white'];
  useEffect(() => {
    let i = currentActive;

    if (intervalRef.current) clearInterval(intervalRef.current);

    // Use 2 seconds if user just moved away from hover, otherwise 5 seconds
    const delay = wasHovered.current ? 2000 : 5000;

    intervalRef.current = setInterval(() => {
      if (!isPaused) {
        i = i + 1;
        if (i === contentPages.length) {
          i = 0;
        }
        setCurrentActive(i);
        wasHovered.current = false; // Reset after auto-advance
      }
    }, delay);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [isPaused, contentPages.length, currentActive]);

  useEffect(() => {
    if (triggeredByButton.current && focusRefs.current[currentActive]) {
      focusRefs.current[currentActive]?.focus();
      triggeredByButton.current = false;
    }
  }, [currentActive]);
  const setPaddingsTop = () => {
    const top = paddings.filter((item) => item.includes('top')).join(',');
    return {
      pt: top.includes('all')
        ? 'desktop.m'
        : top.includes('half')
        ? {
            base: 'xs',
            lg: `calc(var(--chakra-space-desktop-s) + var(${
              contentPages.length === 1 ? `--chakra-space-desktop-xl` : `--chakra-space-desktop-l`
            }) + ${headline !== '' ? '44px' : '0px'})`,
          }
        : {
            base: 's',
            lg: `calc(var(--chakra-space-desktop-l) + var(${
              contentPages.length === 1 ? `--chakra-space-desktop-xl` : `--chakra-space-desktop-l`
            }) + ${headline !== '' ? '44px' : '0px'})`,
          },
    };
  };
  const setPaddingsBottom = () => {
    const bottom = paddings.filter((item) => item.includes('bottom')).join(',');
    return {
      pb: bottom.includes('all')
        ? 0
        : bottom.includes('half')
        ? { base: 'xs', lg: 'desktop.s' }
        : { base: 's', lg: 'desktop.l' },
    };
  };
  const HeadlineBlock = () => {
    const urlByPath = router.asPath.slice(0, router.asPath.lastIndexOf('/'));
    const urlCategory = model.elements.ctaTaxonomySegment?.taxonomyGroup;
    const urlCategoryValue = model.elements.ctaTaxonomySegment?.value[0]?.codename;
    let url =
      urlByPath.length && overviewPages.includes(urlByPath.split('/')[1])
        ? urlByPath
        : `/${translations.multiple(model.elements.content.linkedItems[0]?.elements.category.value[0]?.codename)}`;
    url = `${url}${
      urlCategory && urlCategoryValue
        ? `?${encodeURIComponent(translations.category(urlCategory.toLowerCase()))}=${encodeURIComponent(
            translations.segment(urlCategoryValue.toLowerCase()).toLowerCase(),
          )}`
        : ''
    }`;
    return (
      <Flex
        position={'relative'}
        bg={{ base: mediaPosition === 'right' ? backgroundColor : 'none', lg: 'none' }}
        pt={{ base: 'mobile.l', lg: '70px' }}
        pb={{ base: '0px', lg: 'desktop.xs' }}
        justifyContent='space-between'
        alignItems='center'
        pl={{ base: 'desktop.xs', lg: '0' }}
        pr={{ base: 'desktop.xs', lg: '0' }}
      >
        <Box display={'inline-flex'}>
          <Box
            w={'2px'}
            mr={'desktop.xs'}
            height={'auto'}
            borderWidth={'0px 1px'}
            borderStyle={'solid'}
            borderColor={'cyan.web'}
            borderRadius={'8px'}
          ></Box>
          <Text
            as='h2'
            textStyle={{ base: 'mobile.body.medium.xs', lg: 'desktop.body.medium.xs' }}
            position={{ base: 'sticky', lg: 'relative' }}
            w={'100%'}
          >
            {hyphenHeadline && hyphenHeadline.toLocaleUpperCase()}
          </Text>
        </Box>

        {/* --- View all button --- */}
        {viewAllText && (
          <Box position='relative'>
            <CustomLink
              /* Since we are following same category and subcategory of the page, setting this as per url works as intended */
              href={url}
              type={LinkType.SecondaryButton}
              weight={'680'}
              iconName='chevronRightForLink'
              ariaLabel={`${translations.aria('viewAll')} ${headline}`}
              locale={router.locale}
              isPrDisabled={true}
            >
              {viewAllText}
            </CustomLink>
          </Box>
        )}
      </Flex>
    );
  };
  const TextBlock = ({ active, page, index }: { active: boolean; page: ContentPage; index: number }) => {
    const url = Object.keys(normalize(page, router.locale, undefined, true, translations))[0];
    // This will return element of tag used for that category of the page, or undefined
    const tagCategory =
      mainSubtypeForOverviewAutomatic[
        page.elements.category.value[0].codename as keyof typeof mainSubtypeForOverviewAutomatic
      ];
    // Extract namespace from tagCategory (e.g. tagsThemeTopic => theme_topic)
    const tag = tagCategory
      ? (translations[toKebabCase(tagCategory).replace('tags_', '')] as Translate)(
          // Get the correct tag codename for category of the page and run it through translation (returns undefined if correct tag is not filled -> will not show tag)
          (page.elements[tagCategory] as Elements.TaxonomyElement<Sector | ThemeTopic | ServiceLine>).value[0]
            ?.codename as string,
        )
      : '';
    const title =
      page.elements.partOfCampaignPage?.linkedItems?.length > 0 && page.elements.summaryManualTitle.value.length > 0
        ? page.elements.summaryManualTitle.value
        : page.elements.summaryTitle.value;
    // contentPage.elements.partOfCampaignPage?.linkedItems?.length > 0 &&
    // htmlResolver(contentPage.elements.summaryTeaserText) != null
    //   ? htmlResolver(contentPage.elements.summaryTeaserText)
    //   : htmlResolver(contentPage.elements.summaryDescription);
    const desc =
      page.elements.partOfCampaignPage?.linkedItems?.length > 0 && htmlResolver(page.elements.summaryTeaserText) != null
        ? page.elements.summaryTeaserText
        : page.elements.summaryDescription;
    return (
      <>
        <Flex
          pointerEvents='auto'
          direction='column'
          flexDirection='column-reverse'
          pb={{ base: '0px' }}
          pt={{ base: 'mobile.m', lg: '0px' }}
          role='group'
          gap={{ base: '10px', lg: '0px' }}
        >
          <Link
            ref={(el) => {
              if (active) {
                focusRefs.current[index] = el; // Pass `index` to TextBlock via props
              }
            }}
            padding={{ base: 0, lg: 'desktop.xs' }}
            margin={{ base: 0, lg: 'calc(-1 * var(--chakra-space-desktop-xs))' }}
            href={url}
            tabIndex={active ? 0 : -1}
            _hover={{ textDecoration: 'none' }}
            textDecoration='none'
            cursor={active ? 'pointer' : 'default'}
            pointerEvents={active ? 'auto' : 'none'}
            onMouseEnter={() => active && setIsPaused(true)}
            onMouseLeave={() => {
              if (active) {
                setIsPaused(false);
                wasHovered.current = true;
              }
            }}
          >
            <Text
              as='h2'
              textStyle={{ base: 'mobile.h4', lg: 'desktop.h4' }}
              pt={{ base: '0px' }}
              textDecoration='none'
              transition='color 0.3s ease-in-out'
              color={active ? 'inherit' : 'gray.500'}
              _groupHover={{
                color: active ? 'cyan.web' : 'inherit',
              }}
              dangerouslySetInnerHTML={{ __html: title }}
            ></Text>
          </Link>
          {!!tag && (
            <Text
              textStyle={{ base: 'mobile.body.s', lg: 'desktop.body.s' }}
              pb={{ lg: 'desktop.xs' }}
              tabIndex={-1}
              color={'mountain.base'}
            >
              {toTitleCase(tag)}
            </Text>
          )}
        </Flex>
        {/* <Link href={url} */}
        <Box
          pb='desktop.xs'
          pt={{ base: 'mobile.m', lg: 'desktop.m' }}
          textStyle={{ base: 'mobile.body.l', lg: 'desktop.body.l' }}
          transition='color 0.3s ease-in-out'
          color={active ? 'inherit' : 'gray.500'}
          _groupHover={{
            color: active ? 'cyan.web' : 'inherit',
          }}
          dangerouslySetInnerHTML={{
            __html: createRichTextHtmlResolver(nodeParser).resolveRichText({
              element: desc,
            }).html,
          }}
        ></Box>
        {/* </Link> */}
        {/* <Box display={{ base: 'none', lg: 'unset' }}>
          <CustomLink
            href={url}
            colorScheme='mountain.base'
            isDisabled={!active}
            type={LinkType.SecondaryButton}
            iconName='chevronRightForLink'
            ariaLabel={`${translations.aria('learnMoreAbout')} ${title}`}
            locale={router.locale}
            tabIndex={active ? 0 : -1}
          >
            {translations.common('learnMore')}
          </CustomLink>
        </Box> */}
        {/* <Box display={{ base: 'unset', lg: 'none' }}>
          <CustomLink
            href={url}
            colorScheme='cyan.web'
            type={LinkType.SecondaryButton}
            iconName='chevronRightForLink'
            ariaLabel={`${translations.aria('learnMoreAbout')} ${title}`}
            locale={router.locale}
            tabIndex={active ? 0 : -1}
          >
            {translations.common('learnMore')}
          </CustomLink>
        </Box> */}
        {/* <Box
          tabIndex={0}
          aria-hidden='true'
          onFocus={() => {
            const btn = document.getElementById('viewAllButton');
            if (btn) (btn as HTMLAnchorElement).focus();
          }}
        /> */}
      </>
    );
  };
  const getImageUrl = (page: ContentPage) => {
    const url = Object.keys(normalize(page, router.locale, undefined, true, translations))[0];
    return url;
  };
  return (
    <Box
      id={model.system.codename}
      mx='auto'
      maxW={{ base: '100vw', lg: backgroundWidth === 'partial' ? '1920px' : moduleMaxWidth }}
      minW={`min(100vw, 1920px)`}
      sx={
        backgroundWidth === 'partial'
          ? {
              bgGradient: {
                base: `linear(to-b, ${colors[0]} 30%, ${colors[1]} 0%)`,
                sm: `linear(to-b, ${colors[0]} 40%, ${colors[1]} 0%)`,
                md: `linear(to-b, ${colors[0]} 45%, ${colors[1]} 0%)`,
                lg: `linear(to-r, ${colors[0]} 50%, ${colors[1]} 0%)`,
                xl: `linear(to-r, ${colors[0]} 55%, ${colors[1]} 0%)`,
              },
            }
          : { bg: backgroundColor }
      }
    >
      <Container px={0}>
        {/* -- Headline mobile -- */}
        {headline !== '' && (
          <Box display={{ base: 'block', lg: 'none' }}>
            <HeadlineBlock />
          </Box>
        )}
        {/* -- Headline mobile end -- */}
        <Container
          paddingInlineEnd={{ base: 0, sm: 0, md: 0, lg: 'desktop.xl' }}
          paddingInlineStart={{ base: 'xs', sm: 'xs', md: 'xs', lg: 'desktop.xl' }}
          zIndex={1}
          h={'auto'}
          position='relative'
          overflow={{ base: contentPages.length > 1 ? 'auto' : 'hidden', lg: 'hidden' }}
          sx={{
            '::-webkit-scrollbar': {
              width: '3px',
              height: '3px',
            },
            '::-webkit-scrollbar-track': {
              width: '3px',
              height: '3px',
            },
            '::-webkit-scrollbar-thumb': {
              background: 'var(--chakra-colors-mountain-20)',
              borderRadius: '0',
            },
            scrollbarColor: 'transparent',
            scrollbarWidth: 'thin',
          }}
        >
          {/* -- Actual Content -- */}
          <Box
            w={{ base: contentPages.length > 1 ? '90%' : '100%', lg: '100%' }}
            h='100%'
            position='relative'
            sx={setPaddingsBottom()}
            zIndex={1}
            id='RambollFeaturedContent'
          >
            {/* -- Headline desktop -- */}

            {/* {headline !== '' && (
              <Box display={{ base: 'none', lg: 'block' }}>
                <HeadlineBlock />
              </Box>
            )} */}
            {/* -- Headline desktop end -- */}
            <Flex
              //pt={{ base: 'xs', md: 's', lg: 'desktop.l' }}
              pr={{ base: 'xs', md: 's', lg: '0' }}
              pb={{ base: 'mobile.s', lg: 'desktop.xs' }}
              sx={{ ...setPaddingsTop() }}
              alignItems={contentPages.length === 1 ? 'flex-start' : 'center'}
              w={contentPages.length === 1 ? { base: '100%', lg: 'auto' } : 'auto'}
              flexDirection={{
                base: 'column-reverse',
                lg: mediaPosition === 'left' && !contentPages[1] ? 'row-reverse' : 'row',
              }}
            >
              {/* -- Content Text -- */}
              <Box
                display={{ base: 'flex' }}
                position='relative'
                alignItems={'center'}
                w={{ base: `100%`, lg: 'calc(30% + 80px)' }}
                h='100%'
                zIndex={1}
              >
                {contentPages.map((page, index) => {
                  return (
                    <Box
                      key={index}
                      w='100%'
                      position={{ base: currentActive === index ? 'relative' : 'absolute' }}
                      opacity={{ base: '1', lg: currentActive === index ? '1' : '0' }}
                      transform={{
                        base: 'none',
                        lg: `translateX(${currentActive === index ? '0' : currentActive < index ? '50%' : '-50%'})`,
                      }}
                      transition='transform 0.5s, opacity 0.3s'
                      sx={mediaPosition === 'left' && !contentPages[1] ? { pl: { lg: 'xl' } } : { pr: { lg: 'xl' } }}
                      top='0'
                      left={{ base: `calc(${100 * index}% + ${40 * index}px)`, lg: '0' }}
                    >
                      <TextBlock active={currentActive === index} page={page} index={index} />
                    </Box>
                  );
                })}
              </Box>
              {/* -- Content Text End -- */}
              {/* -- Image -- */}
              <Box w={{ base: '100%', lg: '70%' }} position='relative' zIndex='0'>
                <Flex
                  transform={{
                    base: `unset`,
                    lg: `translateX(calc(-${100 * currentActive}% - ${40 * currentActive}px))`,
                  }}
                  transition='transform 0.6s'
                >
                  {contentPages.map((page, index) => {
                    const ctaLink = getImageUrl(page);
                    const image = imageFormatter(
                      page.elements.summaryImageFrontify?.value,
                      page.elements.summaryAlternativeText?.value,
                    );
                    const summaryTitle = page.elements.summaryTitle?.value;
                    return (
                      <AspectRatio
                        key={index}
                        w='100%'
                        h='100%'
                        ratio={16 / 9}
                        opacity={{ base: '1', lg: index < currentActive ? '0' : '1' }}
                        transition={index < currentActive ? 'opacity 0.4s' : 'opacity 0.8s'}
                        position={index === 0 ? 'relative' : 'absolute'}
                        left={`calc(${100 * index}% + ${40 * index}px)`}
                        overflow='hidden'
                      >
                        <Link
                          href={ctaLink}
                          aria-label={image.alt || summaryTitle}
                          tabIndex={index === currentActive ? 0 : -1}
                          display='block'
                          w='100%'
                          h='100%'
                          _hover={{
                            '& img': {
                              transform: 'scale(1.05)',
                            },
                          }}
                          onMouseEnter={() => index === currentActive && setIsPaused(true)}
                          onMouseLeave={() => {
                            if (index === currentActive) {
                              setIsPaused(false);
                              wasHovered.current = true;
                            }
                          }}
                        >
                          <Image
                            src={image.src}
                            width={image.width}
                            height={image.height}
                            alt={image.alt || summaryTitle}
                            sizes={currentActive === index ? '(max-width: 1024px) 100vw, 40vw' : '5vw'}
                            style={{ transition: 'transform 0.4s ease-in-out' }}
                            // tabIndex={index === currentActive ? 0 : -1}
                          />
                        </Link>
                      </AspectRatio>
                    );
                  })}
                </Flex>
              </Box>
              {/* -- Image End -- */}
            </Flex>
            {/* -- Buttons -- */}
            {contentPages.length > 1 && (
              <Flex
                w='auto'
                gap='desktop.xxs'
                position='relative'
                left='100%'
                transform='translateX(-130px)'
                marginTop='0px'
                zIndex={2}
                display={{ base: 'none', lg: 'flex' }}
              >
                <Box
                  as='button'
                  // tabIndex={-1}
                  disabled={currentActive === 0}
                  w='60px'
                  h='60px'
                  cursor={currentActive === 0 ? 'default' : 'pointer'}
                  aria-label={translations.aria('moveToPrevious')}
                  aria-controls={'RambollFeaturedContent'}
                  onClick={() => {
                    triggeredByButton.current = true;
                    setCurrentActive(currentActive - 1);
                  }}
                >
                  <IconComp iconName='prevLarge' styleName={currentActive === 0 ? 'disabled' : 'default'} />
                </Box>
                <Box
                  as='button'
                  // tabIndex={-1}
                  disabled={currentActive === contentPages.length - 1}
                  w='60px'
                  h='60px'
                  cursor={currentActive === contentPages.length - 1 ? 'default' : 'pointer'}
                  aria-label={translations.aria('moveToNext')}
                  aria-controls={'RambollFeaturedContent'}
                  onClick={() => {
                    triggeredByButton.current = true;
                    setCurrentActive(currentActive + 1);
                  }}
                >
                  <IconComp
                    iconName='nextLarge'
                    styleName={currentActive === contentPages.length - 1 ? 'disabled' : 'default'}
                  />
                </Box>
              </Flex>
            )}
            {/* -- Buttons End -- */}
            <Box
              position='absolute'
              top={0}
              left={0}
              width='100%'
              zIndex={3}
              display={{ base: 'none', lg: 'block' }}
              aria-hidden={false}
            >
              <HeadlineBlock />
            </Box>
          </Box>
          {/* -- Actual Content End -- */}
          {/* -- Design Content -- */}
          <Flex
            aria-hidden={true}
            w={{ base: contentPages.length > 1 ? `${contentPages.length * 90}vw` : '100%', lg: '100%' }}
            h='100%'
            position='absolute'
            top='0'
            left='0'
            margin='0'
            padding='0'
            flexDirection={{
              base: 'column',
              lg: mediaPosition === 'left' && !contentPages[1] ? 'row-reverse' : 'row',
            }}
          >
            <Box
              w={{ base: '100%', lg: '45%' }}
              h={{ base: '20%', lg: '100%' }}
              bg={{
                base: mediaPosition === 'right' || backgroundWidth === 'full' ? backgroundColor : 'white',
                lg: backgroundColor,
              }}
            ></Box>
            <Box
              w={{ base: '100%', lg: '55%' }}
              h='100%'
              bg={{
                base: mediaPosition === 'left' || backgroundWidth === 'full' ? backgroundColor : 'white',
                lg: backgroundWidth === 'full' ? backgroundColor : 'white',
              }}
            ></Box>
          </Flex>
          <Box
            position='absolute'
            h='100%'
            w={backgroundWidth === 'full' ? '100%' : '40%'}
            left={mediaPosition === 'right' || (backgroundWidth !== 'full' && contentPages[1]) ? '0' : 'unset'}
            right={mediaPosition === 'left' && !contentPages[1] ? '0' : 'unset'}
            top='0'
            bg={backgroundColor}
            display={{ base: 'none', lg: 'unset' }}
          ></Box>
          {/* -- Design Content End -- */}
        </Container>
        {/* -- Space under scrollbar -- */}
        <Box display={{ base: 'block', lg: 'none' }} p={'xs'} />
        {/* -- Space under scrollbar -- */}
      </Container>
    </Box>
  );
};
